import { useContext, useState, useEffect } from "react";
import {
    Input,
    Button,
    Card,
    Typography,
    Modal,
    Spin,
    message,
    Table,
    Badge,
    Avatar,
} from "antd";
import {
    CalendarOutlined,
    FileTextOutlined,
    ClockCircleOutlined,
    SearchOutlined,
    UserOutlined,
    IdcardOutlined,
    FieldTimeOutlined,
    PhoneOutlined,
    MobileOutlined,
    AuditOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";
import dayjs from "dayjs";
import LeaveCalendar from "../../components/employee/LeaveCalendar";
import ViewOneEmployeeRecord from "../../components/hr/ViewOneEmployeeRecord";
import ViewOneEmpPerm from "../../components/ViewOneEmpPerm";
import ViewEmpAttendance from "../../components/attendance/viewEmpAttendance";

const { Title, Text } = Typography;

function OnDutyModalContent({ employee, backUrl, authData }) {
    const empId = employee?.employee?.id || employee?.id;
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState("ALL");

    const fetchOnDuty = async () => {
        if (!empId) return;
        setLoading(true);
        try {
            const res = await axios.get(`${backUrl}/api/hr/onduty/?employee=${empId}`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setRecords(res.data.results || res.data || []);
        } catch (err) {
            console.error("Failed to fetch on-duty records", err);
            message.error("Failed to load on-duty records");
            setRecords([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOnDuty();
    }, [empId]);

    const filteredRecords = statusFilter === "ALL"
        ? records
        : records.filter(r => (r.status || "").toUpperCase() === statusFilter);

    const formatDate = (record) => {
        const val = record?.start_time || record?.date || record?.from_date || record?.created_at;
        if (!val) return "-";
        const d = dayjs(val);
        return d.isValid() ? d.format("DD/MM/YYYY") : "-";
    };

    const formatTime = (timeVal) => {
        if (!timeVal) return "-";
        const str = String(timeVal).trim();
        if (!str) return "-";

        const dIso = dayjs(str);
        if (dIso.isValid() && (str.includes("T") || str.includes("-"))) {
            return dIso.format("hh:mm A");
        }

        if (/^\d{1,2}:\d{2}\s*(AM|PM|am|pm)$/i.test(str)) {
            return str.toUpperCase();
        }

        const dTime = dayjs(`2026-01-01 ${str}`);
        if (dTime.isValid()) {
            return dTime.format("hh:mm A");
        }

        return str;
    };

    const statusBadge = (status) => {
        const map = { APPROVED: "success", PENDING: "warning", REJECTED: "error" };
        return <Badge status={map[(status || "").toUpperCase()] || "default"} text={status || "-"} />;
    };

    const columns = [
        {
            title: "Date",
            key: "date",
            render: (_, r) => formatDate(r),
            width: 110,
        },
        {
            title: "Type",
            dataIndex: "onduty_type",
            key: "onduty_type",
            render: v => v || "-",
            width: 140,
        },
        {
            title: "Start Time",
            key: "start_time",
            render: (_, r) => formatTime(r.start_time || r.from_time || r.check_in_time),
            width: 110,
        },
        {
            title: "End Time",
            key: "end_time",
            render: (_, r) => formatTime(r.end_time || r.to_time || r.check_out_time),
            width: 110,
        },
        {
            title: "Location",
            dataIndex: "location",
            key: "location",
            render: v => v || "-",
        },
        {
            title: "Remarks",
            dataIndex: "remarks",
            key: "remarks",
            render: v => v || "-",
            ellipsis: true,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 110,
            render: v => statusBadge(v),
        },
    ];

    const counts = {
        PENDING: records.filter(r => (r.status || "").toUpperCase() === "PENDING").length,
        APPROVED: records.filter(r => (r.status || "").toUpperCase() === "APPROVED").length,
        REJECTED: records.filter(r => (r.status || "").toUpperCase() === "REJECTED").length,
    };

    return (
        <div style={{ padding: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {["ALL", "PENDING", "APPROVED", "REJECTED"].map(s => (
                        <Button
                            key={s}
                            size="small"
                            type={statusFilter === s ? "primary" : "default"}
                            onClick={() => setStatusFilter(s)}
                            style={statusFilter === s ? { backgroundColor: "#0e497b", borderColor: "#0e497b" } : {}}
                        >
                            {s === "ALL" ? `All (${records.length})` : `${s.charAt(0) + s.slice(1).toLowerCase()} (${counts[s] || 0})`}
                        </Button>
                    ))}
                </div>
                <Button size="small" icon={<ReloadOutlined />} onClick={fetchOnDuty} loading={loading}>
                    Refresh
                </Button>
            </div>

            {loading ? (
                <div style={{ textAlign: "center", padding: 40 }}><Spin tip="Loading on-duty records..." /></div>
            ) : filteredRecords.length === 0 ? (
                <div style={{ textAlign: "center", color: "#8c8c8c", padding: 40 }}>No on-duty records found.</div>
            ) : (
                <Table
                    dataSource={filteredRecords}
                    columns={columns}
                    rowKey="id"
                    size="small"
                    bordered
                    pagination={{ pageSize: 8, showSizeChanger: false }}
                    scroll={{ x: 750 }}
                />
            )}
        </div>
    );
}

export default function QuickView() {
    const [empCode, setEmpCode] = useState("");
    const { authData, backUrl } = useContext(AuthContext);
    const [employees, setEmployees] = useState([]); // all matching records
    const [selectedEmployee, setSelectedEmployee] = useState(null); // record used by modals
    const [loading, setLoading] = useState(false);
    const [activeModal, setActiveModal] = useState(null);

    const fetchEmployees = async () => {
        if (!empCode.trim()) {
            message.warning("Please enter an employee code.");
            return;
        }
        setLoading(true);
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/employees?emp_code=${empCode}`,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );
            const results = res.data.results || [];
            if (results.length === 0) {
                message.error("No employee found with that code.");
                setEmployees([]);
            } else {
                setEmployees(results);
                message.success(
                    results.length > 1
                        ? `${results.length} employees found!`
                        : "Employee found!"
                );
            }
        } catch (err) {
            message.error("Failed to fetch employee. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const openModal = (employee, key) => {
        setSelectedEmployee(employee);
        setActiveModal(key);
    };

    const closeModal = () => setActiveModal(null);

    // Used by the modal titles
    const emp = selectedEmployee?.employee ?? selectedEmployee;

    const actionButtons = [
        {
            key: "calendar",
            label: "Calendar",
            icon: <CalendarOutlined />,
            color: "#0e497b",
            bgColor: "#f0f7ff",
            borderColor: "#bae0ff",
        },
        {
            key: "leaves",
            label: "Leaves",
            icon: <FileTextOutlined />,
            color: "#137333",
            bgColor: "#f6ffed",
            borderColor: "#b7eb8f",
        },
        {
            key: "permissions",
            label: "Permissions",
            icon: <ClockCircleOutlined />,
            color: "#d97706",
            bgColor: "#fffbe6",
            borderColor: "#ffe58f",
        },
        {
            key: "attendance",
            label: "Attendance",
            icon: <FieldTimeOutlined />,
            color: "#6b21a8",
            bgColor: "#f9f5ff",
            borderColor: "#e9d5ff",
        },
        {
            key: "onduty",
            label: "On-Duty",
            icon: <AuditOutlined />,
            color: "#0284c7",
            bgColor: "#f0f9ff",
            borderColor: "#bae6fd",
        },
    ];

    const infoTileStyle = {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        background: "rgba(255, 255, 255, 0.12)",
        padding: "8px 12px",
        borderRadius: "8px",
        fontSize: "13px",
    };

    return (
        <div style={{
            minHeight: "85vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            padding: "32px 16px",
            background: "#f4f7fa",
            fontFamily: "'DM Sans', sans-serif"
        }}>
            <style>{`
                .qv-action-btn-hover:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(0,0,0,0.08) !important;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
            <Card
                bordered={false}
                style={{
                    width: "100%",
                    maxWidth: "680px",
                    borderRadius: "16px",
                    boxShadow: "0 8px 32px rgba(14, 73, 123, 0.08)",
                    background: "#ffffff",
                    padding: "12px 16px"
                }}
            >
                {/* Header */}
                <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "24px" }}>
                    <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: "12px",
                        backgroundColor: "#0e497b",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#ffffff",
                        fontSize: "22px",
                        boxShadow: "0 4px 12px rgba(14, 73, 123, 0.25)"
                    }}>
                        <IdcardOutlined />
                    </div>
                    <div>
                        <Title level={4} style={{ margin: 0, color: "#0e497b", fontWeight: 700 }}>
                            Quick View
                        </Title>
                        <Text type="secondary" style={{ fontSize: "13px" }}>
                            Search employee by code to quickly view details, leaves, calendar, attendance & on-duty records.
                        </Text>
                    </div>
                </div>

                {/* Search Bar */}
                <div style={{ display: "flex", gap: "10px", marginBottom: "24px" }}>
                    <Input
                        size="large"
                        placeholder="Enter Employee Code (e.g. EMP001)"
                        value={empCode}
                        onChange={(e) => setEmpCode(e.target.value)}
                        onPressEnter={fetchEmployees}
                        prefix={<UserOutlined style={{ color: "#0e497b" }} />}
                        style={{ borderRadius: "10px" }}
                        allowClear
                    />
                    <Button
                        type="primary"
                        size="large"
                        icon={<SearchOutlined />}
                        onClick={fetchEmployees}
                        loading={loading}
                        style={{
                            borderRadius: "10px",
                            backgroundColor: "#0e497b",
                            borderColor: "#0e497b",
                            paddingLeft: "24px",
                            paddingRight: "24px",
                            fontWeight: 600
                        }}
                    >
                        Search
                    </Button>
                </div>

                {/* One card + action row per matching employee */}
                {employees.map((employee, index) => {
                    const e = employee?.employee ?? employee;
                    return (
                        <div
                            key={e?.id ?? index}
                            style={{
                                animation: "fadeIn 0.3s ease",
                                marginBottom: index === employees.length - 1 ? 0 : 28,
                            }}
                        >
                            {/* Employee Card */}
                            <div style={{
                                background: "linear-gradient(135deg, #0e497b 0%, #155d9b 100%)",
                                borderRadius: "14px",
                                padding: "20px",
                                color: "#ffffff",
                                boxShadow: "0 6px 18px rgba(14, 73, 123, 0.2)",
                                marginBottom: "20px"
                            }}>
                                <div style={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
                                    <Avatar
                                        size={56}
                                        src={e?.photo}
                                        icon={!e?.photo && <UserOutlined />}
                                        style={{
                                            backgroundColor: "rgba(255, 255, 255, 0.2)",
                                            border: "2px solid rgba(255, 255, 255, 0.4)",
                                            flexShrink: 0
                                        }}
                                    />
                                    <div style={{ flexGrow: 1 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                                            <h3 style={{ margin: 0, color: "#ffffff", fontSize: "18px", fontWeight: 700 }}>
                                                {e?.first_name || e?.name || "Employee"} {e?.last_name || ""}
                                            </h3>
                                            <span style={{
                                                backgroundColor: "rgba(255, 255, 255, 0.2)",
                                                padding: "2px 10px",
                                                borderRadius: "20px",
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                letterSpacing: "0.5px"
                                            }}>
                                                {e?.emp_code || empCode}
                                            </span>
                                        </div>

                                        <div style={{ marginTop: "4px", fontSize: "13px", opacity: 0.9 }}>
                                            {e?.designation_name || "-"} | {e?.department_name || "-"}
                                        </div>
                                        <div style={{ fontSize: "12px", opacity: 0.8, marginTop: "2px" }}>
                                            {e?.organisation_name || "-"}
                                        </div>
                                        {employees.length > 1 && (
                                            <div style={{ fontSize: "12px", opacity: 0.8, marginTop: "2px" }}>
                                                ID: {e?.id ?? "-"}{e?.status ? ` | ${e.status}` : ""}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Contact Details Grid */}
                                <div style={{
                                    marginTop: "16px",
                                    paddingTop: "14px",
                                    borderTop: "1px solid rgba(255, 255, 255, 0.2)",
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "10px"
                                }}>
                                    <div style={infoTileStyle}>
                                        <MobileOutlined style={{ fontSize: "15px" }} />
                                        <div>
                                            <span style={{ opacity: 0.8, fontSize: "11px", display: "block" }}>Personal Mobile</span>
                                            <strong>{e?.mobile_number || "N/A"}</strong>
                                        </div>
                                    </div>

                                    <div style={infoTileStyle}>
                                        <PhoneOutlined style={{ fontSize: "15px" }} />
                                        <div>
                                            <span style={{ opacity: 0.8, fontSize: "11px", display: "block" }}>CUG Mobile Number</span>
                                            <strong>{e?.official_cug_number || "N/A"}</strong>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons Row */}
                            <div style={{ display: "flex", gap: "10px", width: "100%", justifyContent: "space-between" }}>
                                {actionButtons.map((btn) => (
                                    <div
                                        key={btn.key}
                                        onClick={() => openModal(employee, btn.key)}
                                        className="qv-action-btn-hover"
                                        style={{
                                            flex: "1 1 0px",
                                            minWidth: "0",
                                            backgroundColor: btn.bgColor,
                                            border: `1px solid ${btn.borderColor}`,
                                            borderRadius: "12px",
                                            padding: "14px 6px",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: "6px",
                                            cursor: "pointer",
                                            transition: "all 0.2s ease-in-out",
                                            boxShadow: "0 2px 6px rgba(0,0,0,0.03)"
                                        }}
                                    >
                                        <div style={{ fontSize: "22px", color: btn.color, display: "flex" }}>
                                            {btn.icon}
                                        </div>
                                        <span style={{
                                            fontSize: "12px",
                                            fontWeight: 600,
                                            color: btn.color,
                                            textAlign: "center",
                                            whiteSpace: "nowrap"
                                        }}>
                                            {btn.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </Card>

            {/* Calendar Modal */}
            <Modal
                title={<><CalendarOutlined style={{ marginRight: 8, color: "#0e497b" }} />Calendar — {emp?.first_name || ""} {emp?.last_name || ""}</>}
                open={activeModal === "calendar"}
                onCancel={closeModal}
                footer={null}
                width={1200}
                destroyOnClose
            >
                {selectedEmployee && <LeaveCalendar employee={selectedEmployee} />}
            </Modal>

            {/* Leaves Modal */}
            <Modal
                title={<><FileTextOutlined style={{ marginRight: 8, color: "#137333" }} />Leave Records — {emp?.first_name || ""} {emp?.last_name || ""}</>}
                open={activeModal === "leaves"}
                onCancel={closeModal}
                footer={null}
                width={1200}
                destroyOnClose
            >
                {selectedEmployee && (
                    <ViewOneEmployeeRecord employee={selectedEmployee} isProfile={false} />
                )}
            </Modal>

            {/* Permissions Modal */}
            <Modal
                title={<><ClockCircleOutlined style={{ marginRight: 8, color: "#d97706" }} />Permissions — {emp?.first_name || ""} {emp?.last_name || ""}</>}
                open={activeModal === "permissions"}
                onCancel={closeModal}
                footer={null}
                width={1200}
                destroyOnClose
            >
                {selectedEmployee && (
                    <ViewOneEmpPerm employee={selectedEmployee} isProfile={false} />
                )}
            </Modal>

            {/* Attendance Modal */}
            <Modal
                title={<><FieldTimeOutlined style={{ marginRight: 8, color: "#6b21a8" }} />View Attendance — {emp?.first_name || ""} {emp?.last_name || ""}</>}
                open={activeModal === "attendance"}
                onCancel={closeModal}
                footer={null}
                width={1200}
                destroyOnClose
            >
                {selectedEmployee && (
                    <ViewEmpAttendance employee={selectedEmployee} isProfile={false} />
                )}
            </Modal>

            {/* On-Duty Modal */}
            <Modal
                title={<><AuditOutlined style={{ marginRight: 8, color: "#0284c7" }} />On-Duty Records — {emp?.first_name || ""} {emp?.last_name || ""}</>}
                open={activeModal === "onduty"}
                onCancel={closeModal}
                footer={null}
                width={1000}
                destroyOnClose
            >
                {selectedEmployee && (
                    <OnDutyModalContent employee={selectedEmployee} backUrl={backUrl} authData={authData} />
                )}
            </Modal>
        </div>
    );
}